import { useCallback, useState } from 'react';
import { generateLoginTokenCloudFunction } from '@/firebase/init';
import { useWorkspaceIdFromParams } from './useWorkspaceIdFromParams';
import { useSearchParams } from 'react-router-dom';
import { useOrganizationStore } from '@/stores/organizationStore';

const backRedirectDomain = `${window.location.protocol}//${window.location.hostname}${
  import.meta.env.DEV ? `:${window.location.port}` : ''
}`;

interface Options {
  OAuthUrl: string;
  platform: string;
  clientID: string;
}

export function useConnectorAuth(
  defaultValChecked: string,
  { OAuthUrl, platform, clientID }: Options,
  additionalParams?: string,
) {
  const [params] = useSearchParams();
  const reconnect = params.get('reconnect') || false;
  const workspaceId = useWorkspaceIdFromParams();
  const [, setSelectedMethod] = useState<string>(defaultValChecked);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );

  const generateOAuthLink = useCallback(
    async (scope: string) => {
      setIsLoading(true);
      const { data } = (await generateLoginTokenCloudFunction({
        workspaceId: workspaceId as string,
        platform,
      })) as {
        data: { encryptedAuthKey: string; version: number };
      };

      const reconnectParam = reconnect ? `&reconnect=true` : '';

      const redirectUrl = encodeURIComponent(import.meta.env._VITE_OAUTH_REDIRECT_URL);
      const state = {
        project_id: `${import.meta.env._VITE_FIREBASE_PROJECT_ID}-${
          selectedOrganization?.id as string
        }`,
        workspace: workspaceId,
        region: selectedOrganization?.region,
        key: [data.encryptedAuthKey, data.version],
        platform: platform,
        url: {
          success: `${backRedirectDomain}/connectors/edit/${platform.replace(
            '_',
            '-',
          )}?workspaceId=${workspaceId}&state=success${
            platform === 'shopify' ? `&shopDomain=${additionalParams}` : ''
          }${reconnectParam}`,
          fail: `${backRedirectDomain}/connectors/error/${platform.replace(
            '_',
            '-',
          )}?workspaceId=${workspaceId}&state=fail`,
        },
      };

      const parsedState = encodeURIComponent(JSON.stringify(state));
      return `${OAuthUrl}?client_id=${clientID}&redirect_uri=${redirectUrl}&state=${parsedState}&scope=${decodeURIComponent(
        scope,
      )}${additionalParams && platform !== 'shopify' ? `&${additionalParams}` : ''}`;
    },
    [
      OAuthUrl,
      additionalParams,
      clientID,
      platform,
      reconnect,
      selectedOrganization?.id,
      selectedOrganization?.region,
      workspaceId,
    ],
  );

  return [setSelectedMethod, workspaceId, isLoading, generateOAuthLink] as const;
}
